import React, { useEffect, useState } from 'react';
import { courseService } from '../../services/courseService';
import { enrollmentService } from '../../services/enrollmentService';
import { videoProgressService } from '../../services/videoProgressService';
import { Course, Enrollment, User } from '../../types';
import { userService } from '../../services/userService';
import { getMediaUrl } from '../../utils/media';

interface CourseWithUsers {
  course: Course;
  enrollments: Enrollment[];
  users: User[];
}

const AdminEnrollmentsPage: React.FC = () => {
  const [coursesWithUsers, setCoursesWithUsers] = useState<CourseWithUsers[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedCourses, setExpandedCourses] = useState<Set<number>>(new Set());

  useEffect(() => {
    fetchAllCoursesWithUsers();
  }, []);

  const fetchAllCoursesWithUsers = async () => {
    setLoading(true);
    try {
      // Lấy tất cả khóa học
      const courseData = await courseService.getCourses(1, 1000, undefined, 'published');
      const allCourses = courseData.courses;

      // Lấy enrollments và users cho mỗi khóa học
      const coursesData: CourseWithUsers[] = [];

      for (const course of allCourses) {
        // Kiểm tra course.id hợp lệ
        if (!course.id || course.id === null || course.id === undefined) {
          console.warn(`Skipping course with invalid id:`, course);
          continue;
        }

        try {
          console.log(`Fetching data for course ${course.id}: ${course.title}`);

          // Thử lấy từ videoProgress (bao gồm cả users đã xem video nhưng chưa có enrollment)
          let users: User[] = [];
          let enrollments: Enrollment[] = [];

          try {
            const courseUsersData = await videoProgressService.getCourseUsers(course.id);
            users = courseUsersData.users || [];
            enrollments = courseUsersData.enrollments || [];
            console.log(`Found ${users.length} users from videoProgress for course ${course.id}`);
          } catch (videoProgressErr: any) {
            console.warn(`Failed to fetch from videoProgress for course ${course.id}:`, videoProgressErr);
            // Fallback: lấy từ enrollments
            try {
              if (course.id && typeof course.id === 'number') {
                enrollments = await enrollmentService.listByCourse(course.id);
                console.log(`Found ${enrollments.length} enrollments for course ${course.id}`);

                // Lấy thông tin user cho mỗi enrollment
                for (const enrollment of enrollments) {
                  try {
                    const user = await userService.getUserProfile(enrollment.user_id);
                    users.push(user);
                    console.log(`Fetched user ${user.id}: ${user.username} for course ${course.id}`);
                  } catch (err) {
                    console.error(`Failed to fetch user ${enrollment.user_id}:`, err);
                  }
                }
              }
            } catch (enrollmentErr: any) {
              console.error(`Failed to fetch enrollments for course ${course.id}:`, enrollmentErr);
            }
          }

          coursesData.push({
            course,
            enrollments,
            users,
          });
        } catch (err: any) {
          console.error(`Failed to fetch data for course ${course.id}:`, err);
          console.error('Error details:', err?.response?.data || err?.message);
          // Vẫn thêm course vào danh sách với enrollments rỗng
          coursesData.push({
            course,
            enrollments: [],
            users: [],
          });
        }
      }

      setCoursesWithUsers(coursesData);
    } catch (err) {
      console.error('Failed to fetch courses:', err);
      alert('Không thể tải danh sách khóa học');
    } finally {
      setLoading(false);
    }
  };

  const toggleCourse = (courseId: number) => {
    setExpandedCourses((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(courseId)) {
        newSet.delete(courseId);
      } else {
        newSet.add(courseId);
      }
      return newSet;
    });
  };

  const handleUnenroll = async (enrollment: Enrollment, courseId: number) => {
    if (!window.confirm('Gỡ người dùng khỏi khóa học?')) return;

    // Nếu enrollment.id là null (chỉ có videoProgress), chỉ xóa khỏi UI
    if (!enrollment.id || enrollment.id === null) {
      setCoursesWithUsers((prev) =>
        prev.map((item) =>
          item.course.id === courseId
            ? {
              ...item,
              enrollments: item.enrollments.filter((e) =>
                !(e.user_id === enrollment.user_id && e.course_id === enrollment.course_id && !e.id)
              ),
              users: item.users.filter((u) => u.id !== enrollment.user_id),
            }
            : item
        )
      );
      alert('Đã gỡ người dùng khỏi danh sách (chưa có enrollment record)');
      return;
    }

    try {
      await enrollmentService.delete(enrollment.id);
      // Cập nhật state
      setCoursesWithUsers((prev) =>
        prev.map((item) =>
          item.course.id === courseId
            ? {
              ...item,
              enrollments: item.enrollments.filter((e) => e.id !== enrollment.id),
              users: item.users.filter((u) => u.id !== enrollment.user_id),
            }
            : item
        )
      );
      alert('Đã gỡ người dùng khỏi khóa học');
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Không thể gỡ người dùng');
    }
  };

  const handleGrantPoints = async (enrollment: Enrollment, courseId: number) => {
    const pointsInput = window.prompt('Nhập số điểm kinh nghiệm muốn cấp:', '10');
    if (!pointsInput) return;

    const points = parseInt(pointsInput, 10);
    if (isNaN(points) || points <= 0) {
      alert('Điểm phải là số dương');
      return;
    }

    // Nếu enrollment.id là null, cần tạo enrollment trước
    let enrollmentId = enrollment.id;
    if (!enrollmentId || enrollmentId === null) {
      try {
        const newEnrollment = await enrollmentService.create(enrollment.user_id, enrollment.course_id);
        enrollmentId = newEnrollment.id;
      } catch (err: any) {
        alert(err?.response?.data?.error || 'Không thể tạo enrollment');
        return;
      }
    }

    try {
      const result = await enrollmentService.grantPoints(enrollmentId, points);
      alert(`Đã cấp ${points} điểm kinh nghiệm. Tổng điểm: ${result.user_level.total_points}`);
      // Refresh data
      fetchAllCoursesWithUsers();
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Không thể cấp điểm');
    }
  };

  const handleUpdateProgress = async (enrollment: Enrollment, progress: number, completed?: boolean) => {
    // Nếu enrollment.id là null (từ videoProgress, chưa có enrollment record), tạo enrollment mới
    if (!enrollment.id || enrollment.id === null) {
      try {
        // Tạo enrollment mới
        const newEnrollment = await enrollmentService.create(enrollment.user_id, enrollment.course_id);
        // Cập nhật progress ngay sau khi tạo
        const updated = await enrollmentService.update(newEnrollment.id, { progress, completed });

        // Cập nhật state
        setCoursesWithUsers((prev) =>
          prev.map((item) => {
            if (item.course.id === enrollment.course_id) {
              // Thay thế enrollment cũ (null id) bằng enrollment mới
              const newEnrollments = item.enrollments.map((e) =>
                (e.user_id === enrollment.user_id && e.course_id === enrollment.course_id && !e.id)
                  ? updated
                  : e
              );
              // Nếu chưa có trong list, thêm vào
              if (!newEnrollments.find(e => e.id === updated.id)) {
                newEnrollments.push(updated);
              }
              return {
                ...item,
                enrollments: newEnrollments,
              };
            }
            return item;
          })
        );
      } catch (err: any) {
        alert(err?.response?.data?.error || 'Không thể tạo enrollment mới');
      }
      return;
    }

    // Nếu đã có enrollment.id, cập nhật bình thường
    try {
      const updated = await enrollmentService.update(enrollment.id, { progress, completed });
      // Cập nhật state
      setCoursesWithUsers((prev) =>
        prev.map((item) => ({
          ...item,
          enrollments: item.enrollments.map((e) => (e.id === enrollment.id ? updated : e)),
        }))
      );
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Không thể cập nhật tiến độ');
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-xl border-2 border-red-200 p-12 text-center">
        <div className="inline-block p-6 bg-red-50 rounded-full mb-4">
          <div className="w-16 h-16 bg-gradient-to-br from-red-400 to-red-600 rounded-full flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-200 border-t-red-600"></div>
          </div>
        </div>
        <p className="text-lg font-bold text-gray-900">Đang tải dữ liệu...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl border-2 border-red-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-500 via-red-600 to-red-500 px-5 sm:px-7 py-6 sm:py-7 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer"></div>
        <div className="relative">
          <h2 className="text-2xl sm:text-4xl font-extrabold text-white mb-3 drop-shadow-lg">Người dùng trong khóa học</h2>
          <p className="text-white text-base sm:text-lg font-bold">
            Tổng số: <span className="text-2xl">{coursesWithUsers.length}</span> khóa học
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="p-5 sm:p-7">
        {coursesWithUsers.length === 0 ? (
          <div className="p-12 text-center">
            <div className="inline-block p-6 bg-red-50 rounded-full mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-red-400 to-red-600 rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-lg"></div>
            </div>
            <p className="text-lg font-bold text-gray-900 mb-1">Không có khóa học nào</p>
            <p className="text-sm text-gray-600">Hãy tạo khóa học mới trước</p>
          </div>
        ) : (
          <div className="space-y-4">
            {coursesWithUsers.map((item) => {
              const isExpanded = expandedCourses.has(item.course.id);
              const userCount = item.users.length;

              return (
                <div
                  key={item.course.id}
                  className="border-2 border-red-200 rounded-xl overflow-hidden hover:border-red-300 transition-all duration-300 bg-white"
                >
                  {/* Course Header */}
                  <div
                    className="bg-gradient-to-r from-red-50 to-red-100 px-5 py-4 cursor-pointer hover:from-red-100 hover:to-red-200 transition-all duration-300"
                    onClick={() => toggleCourse(item.course.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-extrabold text-gray-900 mb-1 truncate">
                          {item.course.title}
                        </h3>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-gray-700 font-semibold">
                            ID: <span className="text-red-600 font-bold">{item.course.id}</span>
                          </span>
                          <span className="text-gray-700 font-semibold">
                            Số người tham gia: <span className="text-red-600 font-bold">{userCount}</span>
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-4 py-2 rounded-full text-sm font-bold ${userCount > 0
                            ? 'bg-green-100 text-green-800 border-2 border-green-300'
                            : 'bg-gray-100 text-gray-600 border-2 border-gray-300'
                          }`}>
                          {userCount > 0 ? `${userCount} người` : 'Chưa có người'}
                        </span>
                        <button
                          className="text-red-600 hover:text-red-700 transition-transform duration-300"
                          style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                        >
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Users List */}
                  {isExpanded && (
                    <div className="border-t-2 border-red-200 bg-white">
                      {userCount === 0 ? (
                        <div className="p-8 text-center text-gray-500">
                          <p className="font-semibold">Chưa có người dùng nào tham gia khóa học này</p>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-full">
                            <thead className="bg-gradient-to-r from-red-100 to-red-200">
                              <tr>
                                <th className="px-5 sm:px-7 py-4 text-left text-sm font-extrabold text-red-800 uppercase tracking-wide border-b-3 border-red-300">
                                  Người dùng
                                </th>
                                <th className="px-5 sm:px-7 py-4 text-left text-sm font-extrabold text-red-800 uppercase tracking-wide border-b-3 border-red-300">
                                  Email
                                </th>
                                <th className="px-5 sm:px-7 py-4 text-left text-sm font-extrabold text-red-800 uppercase tracking-wide border-b-3 border-red-300">
                                  Tiến độ
                                </th>
                                <th className="px-5 sm:px-7 py-4 text-left text-sm font-extrabold text-red-800 uppercase tracking-wide border-b-3 border-red-300">
                                  Hoàn thành
                                </th>
                                <th className="px-5 sm:px-7 py-4 text-left text-sm font-extrabold text-red-800 uppercase tracking-wide border-b-3 border-red-300">
                                  Thao tác
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y-2 divide-red-100">
                              {item.enrollments.map((enrollment) => {
                                const user = item.users.find((u) => u.id === enrollment.user_id);
                                if (!user) return null;

                                return (
                                  <tr key={enrollment.id} className="hover:bg-red-50/50 transition-colors">
                                    <td className="px-5 sm:px-7 py-4">
                                      <div className="flex items-center gap-3">
                                        {user.avatar ? (
                                          <img
                                            src={getMediaUrl(user.avatar)}
                                            alt={user.username}
                                            className="h-10 w-10 rounded-full object-cover ring-2 ring-red-200 shadow-md"
                                          />
                                        ) : (
                                          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-red-400 to-red-600 flex items-center justify-center text-white font-bold shadow-md">
                                            {user.username.charAt(0).toUpperCase()}
                                          </div>
                                        )}
                                        <div>
                                          <div className="font-bold text-gray-900 text-base">{user.username}</div>
                                          {user.full_name && (
                                            <div className="text-sm text-gray-600 font-semibold">{user.full_name}</div>
                                          )}
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-5 sm:px-7 py-4">
                                      <div className="text-base text-gray-900 font-semibold">{user.email}</div>
                                    </td>
                                    <td className="px-5 sm:px-7 py-4">
                                      <div className="flex items-center gap-2">
                                        <input
                                          type="number"
                                          className="w-24 border-3 border-red-300 rounded-xl px-3 py-2 text-base focus:ring-4 focus:ring-red-400 focus:border-red-500 transition-all font-semibold outline-none"
                                          value={Math.round(enrollment.progress || 0)}
                                          min={0}
                                          max={100}
                                          onChange={(e) => {
                                            const v = Math.max(0, Math.min(100, Number(e.target.value)));
                                            handleUpdateProgress(enrollment, v, enrollment.completed);
                                          }}
                                        />
                                        <span className="text-base font-bold text-gray-700">%</span>
                                      </div>
                                    </td>
                                    <td className="px-5 sm:px-7 py-4">
                                      <label className="inline-flex items-center cursor-pointer">
                                        <input
                                          type="checkbox"
                                          className="w-5 h-5 text-red-600 border-2 border-red-300 rounded focus:ring-red-400 focus:ring-2"
                                          checked={!!enrollment.completed}
                                          onChange={(e) =>
                                            handleUpdateProgress(enrollment, enrollment.progress || 0, e.target.checked)
                                          }
                                        />
                                        <span className="ml-2 text-sm font-semibold text-gray-700">
                                          {enrollment.completed ? 'Đã hoàn thành' : 'Chưa hoàn thành'}
                                        </span>
                                      </label>
                                    </td>
                                    <td className="px-5 sm:px-7 py-4">
                                      <div className="flex flex-col gap-2">
                                        <button
                                          onClick={() => handleGrantPoints(enrollment, item.course.id)}
                                          className="px-4 py-2 rounded-xl bg-gradient-to-r from-green-500 to-green-600 text-white text-sm font-bold hover:from-green-600 hover:to-green-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 active:translate-y-0 border-2 border-green-400"
                                          title="Cấp điểm kinh nghiệm"
                                        >
                                          Cấp điểm
                                        </button>
                                        <button
                                          onClick={() => handleUnenroll(enrollment, item.course.id)}
                                          className="px-4 py-2 rounded-xl bg-gradient-to-r from-red-500 to-red-600 text-white text-sm font-bold hover:from-red-600 hover:to-red-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 active:translate-y-0 border-2 border-red-400"
                                        >
                                          Gỡ khỏi khóa học
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminEnrollmentsPage;
